/**
 * Caregiver wage estimator for the Spitex Angehörigenpflege funnel.
 *
 * The Angehörigenpflege model: a family caregiver gets employed and paid an
 * hourly wage to care for a relative, billed to Swiss health insurers. The
 * winning competitor funnel (Pflegewegweiser, Familienspitex, AsFam) anchors a
 * big annual figure via a wage-calculator quiz, then captures contact details.
 * Our wedge is running that quiz INSIDE WhatsApp. This module is the pure,
 * deterministic core the in-chat quiz calls to produce the estimate.
 *
 * SCOPE BOUNDARY: Selenos is the lead-generation VENDOR. Spitex Frei is the
 * client. The hourly rate, the served cantons, and the billable-task list are
 * all the CLIENT's business inputs (WIP inputs I1 and I3). Everything in the
 * CONFIG block below is a PLACEHOLDER until Spitex Frei provides the real
 * figures. We never invent the wage Spitex Frei pays.
 *
 * The output is always framed as an ESTIMATE ("möglicher Anspruch / Schätzung"),
 * never a guaranteed wage — see `WAGE_ESTIMATE_DISCLAIMER_DE`.
 */

import type { Result } from '@selenos/leads';
import { z } from 'zod';

import { SwissCantonSchema, type SwissCanton } from './config';

// ===========================================================================
// CONFIG — PLACEHOLDER values. Replace with Spitex Frei's real inputs.
// ===========================================================================

/**
 * Hourly Angehörigenpflege caregiver rate, in CHF.
 *
 * Confirmed advertisable max CHF 40/h (Osman 2026-06-06); underlying band
 * 33-42, always shown as Schätzung/bis zu. We advertise "bis zu CHF 40.–/Std."
 * as the advertisable ceiling. This is still a single flat figure pending the
 * full per-canton / per-task model (inputs I1-I3); the output stays framed as
 * an estimate, never a guaranteed wage.
 */
export const PLACEHOLDER_HOURLY_RATE_CHF = 40;

/**
 * Cantons in which the estimate is offered.
 *
 * // PLACEHOLDER list — replace with Spitex Frei's actual served-canton
 * footprint (input I2 / I3) before production. Mirrors the 7 cantons the
 * supply conversation config currently advertises; an estimate is only
 * returned for a supported canton, otherwise the result is `not_eligible`.
 */
export const PLACEHOLDER_SUPPORTED_CANTONS: readonly SwissCanton[] = [
  'ZH',
  'AG',
  'BE',
  'ZG',
  'SG',
  'SZ',
  'LU',
] as const;

/**
 * Care tasks that count as billable Grundpflege for the estimate. At least one
 * must be selected for the lead to be eligible — a person who selects none is
 * not (yet) performing reimbursable caregiving.
 *
 * // PLACEHOLDER list — replace with Spitex Frei's actual acceptance criteria
 * (input I3) before production. Keys are stable identifiers the quiz emits;
 * German labels for display live in `BILLABLE_TASK_LABELS_DE`.
 */
export const PLACEHOLDER_BILLABLE_TASKS: readonly string[] = [
  'waschen', // washing / personal hygiene
  'ankleiden', // dressing
  'mobilisation', // helping the person move / transfer
  'essen_eingeben', // assistance with eating
  'koerperpflege', // body care / grooming
  'toilettengang', // toileting assistance
  'medikamente', // medication assistance
] as const;

/**
 * German display labels for the billable tasks (for the quiz's interactive
 * list/buttons and the estimate explanation). // PLACEHOLDER — align wording
 * with Spitex Frei's intake vocabulary (I3).
 */
export const BILLABLE_TASK_LABELS_DE: Record<string, string> = {
  waschen: 'Waschen / Körperhygiene',
  ankleiden: 'An- und Auskleiden',
  mobilisation: 'Mobilisation / Transfer',
  essen_eingeben: 'Hilfe beim Essen',
  koerperpflege: 'Körperpflege',
  toilettengang: 'Hilfe beim Toilettengang',
  medikamente: 'Medikamenten-Unterstützung',
};

/**
 * Upper bound on plausible daily caregiving hours. A relative cannot bill more
 * than 24h/day, and values above this are almost certainly an input error; we
 * clamp to keep the anchored figure credible (a wildly inflated number reads as
 * a scam and fails brand-qa). // PLACEHOLDER — Spitex Frei may cap reimbursable
 * daily hours lower (I3).
 */
export const MAX_HOURS_PER_DAY = 12;

/** Days per year used to annualise. Calendar year, caregiving is daily. */
const DAYS_PER_YEAR = 365;

/**
 * User-facing estimate disclaimer (German). Framed as a possible claim /
 * estimate, dependent on Spitex Frei — never a guaranteed wage. Required by
 * WIP P1-C (compliance framing).
 */
export const WAGE_ESTIMATE_DISCLAIMER_DE =
  'Dies ist eine unverbindliche Schätzung Ihres möglichen Lohnanspruchs, ' +
  'abhängig von der Prüfung und den Konditionen von Spitex Frei. Es ist ' +
  'keine Lohnzusage.';

// ===========================================================================
// Input / output contracts
// ===========================================================================

/**
 * Input to the wage estimate. `canton` is validated against the full Swiss
 * canton enum (any of the 26 is a well-formed input); eligibility against the
 * supported-canton subset is decided inside the estimator, not at the schema
 * boundary, so an unsupported-but-valid canton yields a `not_eligible` result
 * rather than a parse error.
 */
export const EstimateCaregiverWageInputSchema = z.object({
  hoursPerDay: z
    .number()
    .nonnegative('hoursPerDay must be zero or positive')
    .describe('Average hours of caregiving per day'),
  tasks: z
    .array(z.string())
    .describe(
      'Care task keys the caregiver performs (see PLACEHOLDER_BILLABLE_TASKS)',
    ),
  canton: SwissCantonSchema.describe('Caregiver canton of residence'),
});

export type EstimateCaregiverWageInput = z.infer<
  typeof EstimateCaregiverWageInputSchema
>;

/**
 * Pre-validation input shape. `canton` is a raw `string` here (not the narrowed
 * `SwissCanton` enum) because real callers — the in-WhatsApp quiz agent via the
 * MCP tool — submit arbitrary user-typed canton codes that have NOT been
 * validated yet. `estimateCaregiverWage` runtime-validates the whole input with
 * `safeParse`, so the narrowing happens inside the function, not at the call
 * boundary. This keeps the leads-side `CaregiverWageEstimator` contract free of
 * any spitex enum import while still being type-compatible.
 */
export interface RawEstimateCaregiverWageInput {
  hoursPerDay: number;
  tasks: string[];
  canton: string;
}

/** Reason a request is not eligible for an estimate. */
export type WageEstimateIneligibilityReason =
  | 'no_billable_tasks'
  | 'unsupported_canton'
  | 'zero_hours';

/** An eligible estimate: a monthly + annual figure plus the inputs used. */
export interface EligibleWageEstimate {
  eligible: true;
  /** Effective hours used after clamping to MAX_HOURS_PER_DAY. */
  hoursPerDayUsed: number;
  /** Whether the requested hours were clamped to the daily maximum. */
  hoursClamped: boolean;
  /** Billable task keys that matched the config allow-list. */
  billableTasks: string[];
  canton: SwissCanton;
  hourlyRateChf: number;
  monthlyEstimateChf: number;
  annualEstimateChf: number;
  /** Always-present estimate disclaimer (German). */
  disclaimerDe: string;
}

/** A not-eligible result: explains why, never a figure. */
export interface IneligibleWageEstimate {
  eligible: false;
  reason: WageEstimateIneligibilityReason;
}

export type WageEstimate = EligibleWageEstimate | IneligibleWageEstimate;

/**
 * Validation error for a malformed input (e.g. NaN hours, unknown canton code).
 * Distinct from `not_eligible`, which is a well-formed but non-qualifying input.
 */
export class WageEstimateError extends Error {
  readonly code = 'INVALID_WAGE_ESTIMATE_INPUT';
  readonly issues: string[];
  constructor(message: string, issues: string[]) {
    super(message);
    this.name = 'WageEstimateError';
    this.issues = issues;
  }
}

// ===========================================================================
// Estimator
// ===========================================================================

/** Round a CHF amount to two decimals (Rappen), avoiding float drift. */
function roundChf(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Estimate a family caregiver's possible monthly + annual Angehörigenpflege
 * wage. Pure and deterministic: no I/O, no clock, no randomness.
 *
 * Formula: hoursPerDay * 365 * hourlyRate, gated on at least one billable
 * Grundpflege task and a supported canton.
 *
 * Returns a `Result`:
 * - `success: true` with a `WageEstimate` that is either `eligible` (a figure)
 *   or `not_eligible` (a reason — empty tasks, unsupported canton, zero hours).
 * - `success: false` with a `WageEstimateError` only for a MALFORMED input
 *   (non-finite hours, unparseable canton). Non-qualifying-but-well-formed
 *   inputs are NOT errors; they are `eligible: false` results so the quiz can
 *   branch into a disqualifier message instead of crashing.
 */
export function estimateCaregiverWage(
  input: RawEstimateCaregiverWageInput,
): Result<WageEstimate, WageEstimateError> {
  const parsed = EstimateCaregiverWageInputSchema.safeParse(input);
  if (!parsed.success) {
    return {
      success: false,
      error: new WageEstimateError(
        'Invalid wage estimate input',
        parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
      ),
    };
  }

  // Guard against non-finite hours. zod's number rejects NaN/Infinity, but be
  // explicit since this figure anchors an ad.
  if (!Number.isFinite(parsed.data.hoursPerDay)) {
    return {
      success: false,
      error: new WageEstimateError('hoursPerDay must be a finite number', [
        'hoursPerDay: not finite',
      ]),
    };
  }

  const { hoursPerDay, tasks, canton } = parsed.data;

  // Eligibility gate 1: canton must be in the supported (placeholder) set.
  if (!PLACEHOLDER_SUPPORTED_CANTONS.includes(canton)) {
    return {
      success: true,
      data: { eligible: false, reason: 'unsupported_canton' },
    };
  }

  // Eligibility gate 2: at least one billable Grundpflege task.
  const billableSet = new Set(PLACEHOLDER_BILLABLE_TASKS);
  const billableTasks = Array.from(
    new Set(tasks.filter((t) => billableSet.has(t))),
  );
  if (billableTasks.length === 0) {
    return {
      success: true,
      data: { eligible: false, reason: 'no_billable_tasks' },
    };
  }

  // Eligibility gate 3: a positive number of hours. Zero billable hours yields
  // no wage, so it is a non-eligible (not error) outcome.
  if (hoursPerDay <= 0) {
    return { success: true, data: { eligible: false, reason: 'zero_hours' } };
  }

  // Clamp implausible hours so the anchored figure stays credible.
  const hoursPerDayUsed = Math.min(hoursPerDay, MAX_HOURS_PER_DAY);
  const hoursClamped = hoursPerDay > MAX_HOURS_PER_DAY;

  const annualEstimateChf = roundChf(
    hoursPerDayUsed * DAYS_PER_YEAR * PLACEHOLDER_HOURLY_RATE_CHF,
  );
  const monthlyEstimateChf = roundChf(annualEstimateChf / 12);

  return {
    success: true,
    data: {
      eligible: true,
      hoursPerDayUsed,
      hoursClamped,
      billableTasks,
      canton,
      hourlyRateChf: PLACEHOLDER_HOURLY_RATE_CHF,
      monthlyEstimateChf,
      annualEstimateChf,
      disclaimerDe: WAGE_ESTIMATE_DISCLAIMER_DE,
    },
  };
}
